import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { logger } from "@fidget/common/utils";
import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import commonStyles from "../../../../../containers/settings/settingsEditCommon.css";
import { useStore } from "../../../../../models";
import settingsStyles from "../../../../../styles/settings.css";
import { RolePicker } from "../../../../forms/rolePicker/rolePicker";
import { TimeValidityRangePicker } from "../../../../forms/validityRangePicker/timeValidityRangePicker";
import { useTimeValidityController } from "../../../../forms/validityRangePicker/useTimeValidityController";
import { NumericStepper } from "../../../../numericStepper/numericStepper";
import { TextButton } from "../../../../textButton/textButton";
import { AdviserGroupEditScreen } from "../../../agentGroups/components/agentGroupEditScreen/agentGroupEditScreen";
import { ExistingAgentGroupForm } from "../../../agentGroups/types";
import { mapAgentGroupToForm } from "../../../agentGroups/utils";
import { SettingsForm } from "../../../settingsForm";
import { SubtitleDescription } from "../../../subtitleDescription";
import { ExistingAdviserForm } from "../../types";
import { mapExistingFormToPatchForms } from "../../utils/utils";
import styles from "./adviserEditScreen.css";
import { TagsTagGroupsInput } from "./tagsTagGroupsInput";
import { DeleteCrossIcon } from "@fidget/common/components/icons/deleteCrossIcon/DeleteCrossIcon";
import { pushIfNotExists } from "@fidget/common/utils/arrayHelpers";
import { useAgentServices } from "../../../../../models/agentServices";

interface Props {
  adviser: ExistingAdviserForm;
  onGoBack(): void;
  reset(item: ExistingAdviserForm): void;
  save(item: ExistingAdviserForm): void;
}

export const AdviserEditScreen = observer(({ adviser, onGoBack, reset, save }: Props) => {
  const { spinnerService } = useAgentServices();

  const validityFormController = useTimeValidityController(adviser);

  const { uiStore, agentGroupStore } = useStore();

  const [wasEdited, setWasEdited] = useState(false);
  const [currentlyEditedAgentGroup, setCurrentlyEditedAgentGroup] =
    useState<ExistingAgentGroupForm>();

  const agentGroups = agentGroupStore.getByIds(adviser.$.agentGroupIds.$);

  useEffect(() => {
    const setEdited = () => setWasEdited(true);

    adviser.$.roleId.onUpdate(setEdited);
    adviser.$.tagGroupIds.onUpdate(setEdited);
    adviser.$.tagIds.onUpdate(setEdited);
    adviser.$.validTo.onUpdate(setEdited);
    adviser.$.validFrom.onUpdate(setEdited);
    adviser.$.autoAssignedLimit.onUpdate(setEdited);
  }, [adviser]);

  const setEditingGroup = (agentGroupId: string) => {
    const group = agentGroupStore.getDTOById(agentGroupId);
    if (group) {
      setCurrentlyEditedAgentGroup(mapAgentGroupToForm(group));
    }
  };

  const handleDiscard = () => {
    reset(adviser);
    setWasEdited(false);
    validityFormController.resetTimeValidity();
  };

  const handleSave = async () => {
    try {
      const validate = await adviser.validate();
      if (validate.hasError) {
        uiStore.addErrorToast(intl.formatMessage("settings.tagGroups.error.wrong"));
        return;
      }

      const form = mapExistingFormToPatchForms(adviser);

      const formWithPermissions = {
        tagGroupIds: uiStore.canDisplayAdvisersAssignments() ? form.tagGroupIds : undefined,
        autoAssignedLimit: uiStore.canDisplayAdvisersAutoAssignedLimit()
          ? form.autoAssignedLimit
          : undefined,
        tagIds: uiStore.canDisplayAdvisersAssignments() ? form.tagIds : undefined,
        roleId: uiStore.canDisplayAdvisersConfiguration() ? form.roleId : undefined,
        validFrom: uiStore.canDisplayAdvisersConfiguration() ? form.validFrom : undefined,
        validTo: uiStore.canDisplayAdvisersConfiguration() ? form.validTo : undefined
      };

      await spinnerService.patchAdviser(adviser.$.id.$, formWithPermissions);

      save(adviser);
      setWasEdited(false);
    } catch (e) {
      logger.error({ message: "Couldn't update adviser", error: e });
      uiStore.addGenericErrorToast();
    }
  };

  const handleGoBack = () => {
    if (wasEdited) {
      uiStore.addErrorToast(intl.formatMessage("settings.widgetConfig.unsavedChanges"));
      return;
    }

    save(adviser);
    onGoBack();
  };

  const handleAgentGroupSave = (group: ExistingAgentGroupForm) => {
    if (!group.$.adviserIds.$.includes(adviser.$.id.$)) {
      adviser.$.agentGroupIds.reset(adviser.$.agentGroupIds.$.filter((g) => g !== group.$.id.$));
    } else {
      adviser.$.agentGroupIds.reset(pushIfNotExists(adviser.$.agentGroupIds.$, group.$.id.$));
    }
  };

  const renderTitle = () => (
    <div className={classNames(commonStyles.title, settingsStyles.pointer)} onClick={handleGoBack}>
      <i className={classNames("icon-arrow", settingsStyles.rotated)} />
      <L10n
        id="settings.advisers.edit.title"
        values={{ firstName: adviser.$.firstName.$, lastName: adviser.$.lastName.$ }}
      />
    </div>
  );

  const renderLimits = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.advisers.edit.autoAssignedLimit")}
        description={intl.formatMessage("settings.advisers.edit.autoAssignedLimit.description")}
      />
      <div className={classNames(commonStyles.content, styles["limit-input-container"])}>
        <NumericStepper
          min={0}
          max={99}
          value={adviser.$.autoAssignedLimit.$}
          onChange={(value) => adviser.$.autoAssignedLimit.reset(value)}
          placeholder="-"
        />
        <DeleteCrossIcon
          onClick={() => {
            adviser.$.autoAssignedLimit.$ = undefined;
            adviser.$.autoAssignedLimit.onChange(undefined);
          }}
        />
      </div>
    </div>
  );

  const renderTags = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.advisers.edit.skills")}
        description={intl.formatMessage("settings.advisers.edit.skills.description")}
      />
      <div className={commonStyles.content}>
        <TagsTagGroupsInput
          tagIds={adviser.$.tagIds}
          tagGroupIds={adviser.$.tagGroupIds}
          onTagDelete={(tagId) =>
            adviser.$.tagIds.reset(adviser.$.tagIds.$.filter((t) => t !== tagId))
          }
          onTagGroupDelete={(id) =>
            adviser.$.tagGroupIds.reset(adviser.$.tagGroupIds.$.filter((t) => t !== id))
          }
        />
      </div>
    </div>
  );

  const renderAdviserGroups = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.advisers.edit.groups")}
        description={intl.formatMessage("settings.advisers.edit.groups.description")}
      />
      <div className={commonStyles.content}>
        <div className={styles["groups-list"]}>
          {agentGroups.map((ag) => (
            <div key={ag.id} className={styles["group-list-element"]}>
              <TextButton text={ag.name} onClick={() => setEditingGroup(ag.id)} />
              {","}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderRole = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.advisers.edit.role")}
        description={intl.formatMessage("settings.advisers.edit.role.description")}
      />
      <div className={commonStyles.content}>
        <RolePicker
          roleId={adviser.$.roleId.$}
          onChange={(role) => adviser.$.roleId.reset(role.id)}
        />
      </div>
    </div>
  );

  const renderTimeValidity = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.advisers.edit.validity")}
        description={intl.formatMessage("settings.advisers.edit.validity.description")}
      />

      <TimeValidityRangePicker
        controller={validityFormController}
        validFrom={adviser.$.validFrom.$}
        validTo={adviser.$.validTo.$}
      />
    </div>
  );

  const renderAdviserScreen = () => (
    <SettingsForm
      disabled={!wasEdited}
      onDiscard={handleDiscard}
      onSubmit={handleSave}
      withoutSaveDiscardButtons={
        !(
          uiStore.canDisplayAdvisersAssignments() ||
          uiStore.canDisplayAdvisersConfiguration() ||
          uiStore.canDisplayAdvisersAutoAssignedLimit()
        )
      }
    >
      {renderTitle()}
      {uiStore.canDisplayAdvisersAutoAssignedLimit() && renderLimits()}
      {uiStore.canDisplayAdvisersAssignments() && renderTags()}
      {(uiStore.canDisplayAgentGroupsConfiguration() || uiStore.canDisplayAdvisersAssignments()) &&
        renderAdviserGroups()}
      {uiStore.canDisplayAdvisersConfiguration() && renderRole()}
      {uiStore.canDisplayAdvisersConfiguration() && renderTimeValidity()}
    </SettingsForm>
  );

  const renderAgentGroupEditScreen = (group: ExistingAgentGroupForm) => (
    <AdviserGroupEditScreen
      group={group}
      onGoBack={() => setCurrentlyEditedAgentGroup(undefined)}
      onDiscard={() => setEditingGroup(group.$.id.$)}
      onSave={handleAgentGroupSave}
    />
  );

  return currentlyEditedAgentGroup
    ? renderAgentGroupEditScreen(currentlyEditedAgentGroup)
    : renderAdviserScreen();
});
